import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import ProductionCalendar from '../ui/ProductionCalendar';
import IndividualAnimalTracker from '../ui/IndividualAnimalTracker';
import { 
  Bird, Egg, Baby, DollarSign, Plus, Edit2, Trash2, 
  TrendingUp, TrendingDown, Minus, Calendar, Search,
  ChevronRight, AlertCircle, CheckCircle2, Clock, Star, Users
} from 'lucide-react';
import { validateDate, validateQuantity } from '../../utils/validation';
import { todayLocal, toLocalDateStr, daysAgoLocal, formatDateShort } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Extract FastAPI error detail (string, array, or object) into a printable string.
const errMsg = (err, fallback = 'Something went wrong') => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || String(e)).join(', ');
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return err?.message || fallback;
};

// Breed options for chickens
const CHICKEN_BREEDS = [
  'Rhode Island Red', 'Leghorn', 'Plymouth Rock', 'Orpington', 'Australorp',
  'Sussex', 'Wyandotte', 'Brahma', 'Cochin', 'Silkie', 'Easter Egger',
  'Ameraucana', 'Marans', 'Welsummer', 'Barnevelder', 'Hamburg', 'Polish',
  'Cornish Cross', 'Freedom Ranger', 'Red Ranger', 'Mixed/Unknown', 'Other'
];

const PURPOSE_OPTIONS = [
  { value: 'layers', label: 'Layers (Egg Production)' },
  { value: 'breeders', label: 'Breeders' },
  { value: 'dual', label: 'Dual-Purpose' },
  { value: 'meat', label: 'Meat Birds' }
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'bg-green-500' },
  { value: 'broody', label: 'Broody', color: 'bg-yellow-500' },
  { value: 'molting', label: 'Molting', color: 'bg-orange-500' },
  { value: 'sold', label: 'Sold', color: 'bg-blue-500' },
  { value: 'culled', label: 'Culled', color: 'bg-gray-500' },
  { value: 'deceased', label: 'Deceased', color: 'bg-red-500' }
];

const PoultryManager = () => {
  const { getAuthHeader } = useAuth();
  const [activeSection, setActiveSection] = useState('flock');
  const [loading, setLoading] = useState(true);
  
  // Deep link state for opening animal profile from dashboard
  const [deepLinkAnimal, setDeepLinkAnimal] = useState(null);
  const [showDeepLinkDialog, setShowDeepLinkDialog] = useState(false);
  
  // Flock state
  const [flocks, setFlocks] = useState([]);
  const [selectedFlock, setSelectedFlock] = useState(null);
  const [showFlockDialog, setShowFlockDialog] = useState(false);
  const [flockForm, setFlockForm] = useState({
    name: '',
    tracking_type: 'cohort',
    breed: '',
    purpose: 'layers',
    hatch_date: '',
    source: '',
    bird_count: 1,
    male_count: 0,
    female_count: 0,
    notes: ''
  });
  const [editingFlock, setEditingFlock] = useState(null);
  
  // Egg tracking state
  const [eggRecords, setEggRecords] = useState([]);
  const [eggStats, setEggStats] = useState(null);
  const [eggCalendarData, setEggCalendarData] = useState({});
  const [showEggDialog, setShowEggDialog] = useState(false);
  const [femaleHens, setFemaleHens] = useState([]); // For individual hen assignment
  const [eggForm, setEggForm] = useState({
    flock_id: '',
    date: todayLocal(),
    count: 0,
    entry_type: 'total',
    breed_breakdown: {},
    notes: '',
    individual_animal_id: ''
  });
  
  // Incubation state
  const [incubationBatches, setIncubationBatches] = useState([]);
  const [showIncubationDialog, setShowIncubationDialog] = useState(false);
  const [incubationForm, setIncubationForm] = useState({
    name: '',
    flock_id: '',
    species: 'chicken',
    start_date: todayLocal(),
    egg_count: 0,
    source: 'incubator', // 'incubator' or 'brooding_hen'
    hen_name: '',
    notes: ''
  });
  
  // Chick batches state
  const [chickBatches, setChickBatches] = useState([]);

  // All individually-tracked poultry birds (across all flocks) — used for
  // the "X in flock · Y tracked individually" badge on flock rows.
  const [allIndividualBirds, setAllIndividualBirds] = useState([]);

  // Fetch data on mount
  useEffect(() => {
    fetchFlocks();
    fetchEggStats();
    fetchIncubationBatches();
    fetchEggCalendar();
    fetchChickBatches();
    fetchAllIndividualBirds();
  }, []);

  // Check for deep link to animal profile from dashboard
  useEffect(() => {
    const handleDeepLink = async () => {
      const storedProfile = localStorage.getItem('openAnimalProfile');
      if (!storedProfile) return;
      
      // Only process if we have flocks loaded
      if (flocks.length === 0) return;
      
      try {
        const { animalId, flockId, isPoultry, timestamp } = JSON.parse(storedProfile);
        
        console.log('Deep link data:', { animalId, flockId, isPoultry, timestamp, age: Date.now() - timestamp });
        
        // Only use if less than 60 seconds old and is a poultry animal
        if (Date.now() - timestamp > 60000 || !isPoultry) {
          console.log('Deep link expired or not poultry');
          localStorage.removeItem('openAnimalProfile');
          return;
        }
        
        // Clear storage first to prevent re-triggering
        localStorage.removeItem('openAnimalProfile');
        
        // Find the flock if we have a flockId
        const flock = flockId ? flocks.find(f => f.id === flockId) : flocks[0];
        
        if (flock) {
          // Select the flock to show its details
          setSelectedFlock(flock);
          setActiveSection('flock');
        }
        
        // Fetch the animal directly and show in our own dialog
        console.log('Fetching animal:', animalId);
        toast.info('Loading profile...');
        
        try {
          const res = await axios.get(
            `${API_URL}/api/animals/individual/${animalId}`,
            { headers: getAuthHeader() }
          );
          console.log('Animal fetched:', res.data);
          if (res.data) {
            setDeepLinkAnimal(res.data);
            // Small delay to ensure state is set
            setTimeout(() => {
              console.log('Opening dialog');
              setShowDeepLinkDialog(true);
            }, 300);
          }
        } catch (fetchError) {
          console.error('Failed to fetch animal for deep link:', fetchError);
          toast.error('Could not load animal profile');
        }
        
      } catch (e) {
        console.error('Deep link error:', e);
      }
    };
    
    handleDeepLink();
  }, [flocks, getAuthHeader]);

  const fetchFlocks = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/poultry/flocks`, {
        headers: getAuthHeader()
      });
      setFlocks(res.data.flocks || []);
      if (res.data.flocks?.length > 0 && !selectedFlock) {
        setSelectedFlock(res.data.flocks[0]);
      }
    } catch (error) {
      console.error('Failed to fetch flocks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllIndividualBirds = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/animals/individual?animal_type=poultry`,
        { headers: getAuthHeader() }
      );
      setAllIndividualBirds(res.data.animals || []);
    } catch (error) {
      console.error('Failed to fetch individual birds:', error);
    }
  };

  const fetchEggStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/poultry/eggs/stats`, {
        headers: getAuthHeader()
      });
      setEggStats(res.data);
    } catch (error) {
      console.error('Failed to fetch egg stats:', error);
    }
  };

  const fetchEggCalendar = async () => {
    try {
      // Fetch 90 days of data for calendar view
      const endDate = todayLocal();
      const startDate = daysAgoLocal(90);
      
      const res = await axios.get(
        `${API_URL}/api/poultry/eggs/calendar?start_date=${startDate}&end_date=${endDate}`,
        { headers: getAuthHeader() }
      );
      setEggCalendarData(res.data);
    } catch (error) {
      console.error('Failed to fetch egg calendar:', error);
    }
  };

  const fetchIncubationBatches = async () => {
    try {
      // Fetch from both old and new APIs for compatibility
      const [oldRes, newRes] = await Promise.all([
        axios.get(`${API_URL}/api/poultry/incubation`, { headers: getAuthHeader() }).catch(() => ({ data: { batches: [] } })),
        axios.get(`${API_URL}/api/incubation`, { headers: getAuthHeader() }).catch(() => ({ data: { batches: [] } }))
      ]);
      
      // Merge and dedupe by id
      const oldBatches = oldRes.data.batches || [];
      const newBatches = newRes.data.batches || [];
      const allBatches = [...newBatches, ...oldBatches.filter(ob => !newBatches.find(nb => nb.id === ob.id))];
      setIncubationBatches(allBatches);
    } catch (error) {
      console.error('Failed to fetch incubation batches:', error);
    }
  };

  const fetchChickBatches = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/chicks`, {
        headers: getAuthHeader()
      });
      setChickBatches(res.data.batches || []);
    } catch (error) {
      console.error('Failed to fetch chick batches:', error);
    }
  };

  const fetchEggRecords = async (flockId) => {
    try {
      const res = await axios.get(`${API_URL}/api/poultry/eggs?flock_id=${flockId}&limit=30`, {
        headers: getAuthHeader()
      });
      setEggRecords(res.data.records || []);
    } catch (error) {
      console.error('Failed to fetch egg records:', error);
    }
  };

  // Fetch female hens for individual egg assignment
  const fetchFemaleHensForFlock = async (flockId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/animals/individual?animal_type=poultry&group_id=${flockId}`,
        { headers: getAuthHeader() }
      );
      // Filter for FEMALES ONLY - only hens can lay eggs
      const femaleOnly = (res.data.animals || []).filter(a => {
        const gender = a.gender || a.sex || 'unknown';
        return gender === 'female';
      });
      setFemaleHens(femaleOnly);
    } catch (error) {
      console.error('Failed to fetch individual hens:', error);
      setFemaleHens([]);
    }
  };

  // Flock CRUD
  const handleCreateFlock = async () => {
    const breedName = flockForm.breed === '__custom__' ? flockForm.customBreed : flockForm.breed;
    if (!breedName) {
      toast.error('Please select or enter a breed');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/poultry/flocks`, {
        ...flockForm,
        breed: breedName
      }, {
        headers: getAuthHeader()
      });
      toast.success('Flock created!');
      setShowFlockDialog(false);
      resetFlockForm();
      fetchFlocks();
    } catch (error) {
      toast.error('Failed to create flock');
    }
  };

  const handleUpdateFlock = async () => {
    const breedName = flockForm.breed === '__custom__' ? flockForm.customBreed : flockForm.breed;
    if (!breedName) {
      toast.error('Please select or enter a breed');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/poultry/flocks/${editingFlock.id}`, {
        ...flockForm,
        breed: breedName
      }, {
        headers: getAuthHeader()
      });
      toast.success('Flock updated!');
      setShowFlockDialog(false);
      setEditingFlock(null);
      resetFlockForm();
      fetchFlocks();
    } catch (error) {
      toast.error('Failed to update flock');
    }
  };

  const handleDeleteFlock = async (flockId) => {
    if (!window.confirm('Delete this flock? This will also delete associated records.')) return;
    try {
      await axios.delete(`${API_URL}/api/poultry/flocks/${flockId}`, {
        headers: getAuthHeader()
      });
      toast.success('Flock deleted');
      fetchFlocks();
      if (selectedFlock?.id === flockId) {
        setSelectedFlock(null);
      }
    } catch (error) {
      toast.error('Failed to delete flock');
    }
  };

  const resetFlockForm = () => {
    setFlockForm({
      name: '',
      tracking_type: 'cohort',
      breed: '',
      customBreed: '',
      purpose: 'layers',
      hatch_date: '',
      source: '',
      bird_count: 1,
      male_count: 0,
      female_count: 0,
      notes: ''
    });
  };

  // Egg tracking
  const handleQuickEggEntry = async (flockId, count) => {
    const q = validateQuantity(count, { field: 'Egg count', max: 5000 });
    if (!q.ok) { toast.error(q.message); return; }
    try {
      await axios.post(`${API_URL}/api/poultry/eggs`, {
        flock_id: flockId,
        date: todayLocal(),
        count: count,
        entry_type: 'total'
      }, {
        headers: getAuthHeader()
      });
      toast.success(`${count} eggs recorded!`);
      fetchEggStats();
      fetchEggCalendar();
      if (selectedFlock?.id === flockId) {
        fetchEggRecords(flockId);
      }
    } catch (error) {
      toast.error(errMsg(error, 'Failed to record eggs'));
    }
  };

  const handleDetailedEggEntry = async () => {
    const q = validateQuantity(eggForm.count, { field: 'Egg count', max: 5000 });
    if (!q.ok) { toast.error(q.message); return; }
    const dv = validateDate(eggForm.date, { field: 'Date' });
    if (!dv.ok && dv.reason !== 'too_old') { toast.error(dv.message); return; }
    try {
      await axios.post(`${API_URL}/api/poultry/eggs`, {
        ...eggForm,
        individual_animal_id: eggForm.individual_animal_id || null,
        allow_historical: !dv.ok && dv.reason === 'too_old'
      }, {
        headers: getAuthHeader()
      });
      toast.success('Egg record saved!');
      setShowEggDialog(false);
      setEggForm({
        flock_id: '',
        date: todayLocal(),
        count: 0,
        entry_type: 'total',
        breed_breakdown: {},
        notes: '',
        individual_animal_id: ''
      });
      setFemaleHens([]);
      fetchEggStats();
      fetchEggCalendar();
    } catch (error) {
      toast.error(errMsg(error, 'Failed to save egg record'));
    }
  };

  // Incubation
  const handleCreateIncubation = async () => {
    try {
      // Use the new incubation API with reminder generation
      const res = await axios.post(`${API_URL}/api/incubation`, {
        name: incubationForm.name,
        flock_id: incubationForm.flock_id || null,
        species: incubationForm.species || 'chicken',
        egg_count: incubationForm.egg_count,
        start_date: incubationForm.start_date,
        source: incubationForm.source,
        hen_name: incubationForm.hen_name || null,
        notes: incubationForm.notes
      }, {
        headers: getAuthHeader()
      });
      
      const msg = incubationForm.source === 'brooding_hen' 
        ? `🐔 ${incubationForm.hen_name || 'Hen'} is sitting! Expected hatch: ${res.data.expected_hatch_date}`
        : `🥚 Incubation started! Expected hatch: ${res.data.expected_hatch_date}`;
      
      toast.success(msg);
      setShowIncubationDialog(false);
      setIncubationForm({
        name: '',
        flock_id: '',
        species: 'chicken',
        start_date: todayLocal(),
        egg_count: 0,
        source: 'incubator',
        hen_name: '',
        notes: ''
      });
      fetchIncubationBatches();
    } catch (error) {
      toast.error('Failed to start incubation');
    }
  };

  const handleRecordHatch = async (batchId, hatchCount) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/incubation/${batchId}/hatch?hatch_count=${hatchCount}&create_chick_batch=true`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`🐥 ${res.data.message} (${res.data.hatch_rate} success rate)`);
      fetchIncubationBatches();
      fetchChickBatches();
    } catch (error) {
      toast.error('Failed to record hatch');
    }
  };

  const handleUpdateIncubationStatus = async (batchId, status, data = {}) => {
    try {
      await axios.put(`${API_URL}/api/incubation/${batchId}`, {
        status,
        ...data
      }, {
        headers: getAuthHeader()
      });
      toast.success('Batch updated!');
      fetchIncubationBatches();
    } catch (error) {
      // Try old API as fallback
      try {
        await axios.put(`${API_URL}/api/poultry/incubation/${batchId}`, {
          status,
          ...data
        }, {
          headers: getAuthHeader()
        });
        toast.success('Batch updated!');
        fetchIncubationBatches();
      } catch {
        toast.error('Failed to update batch');
      }
    }
  };

  // Calculate days until events
  const getDaysUntil = (dateStr) => {
    const target = new Date(dateStr);
    const today = new Date();
    const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Format incubation dates (21 days for chickens)
  const getIncubationDates = (setDate) => {
    const set = new Date(setDate);
    const lockdown = new Date(set);
    lockdown.setDate(lockdown.getDate() + 18);
    const hatch = new Date(set);
    hatch.setDate(hatch.getDate() + 21);
    return {
      lockdown: toLocalDateStr(lockdown),
      hatch: toLocalDateStr(hatch)
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground flex items-center gap-3">
            <Bird className="w-8 h-8 text-primary" />
            Poultry Manager
          </h1>
          <p className="text-muted-foreground mt-1">
            Track your flock, eggs, and incubation
          </p>
        </div>
      </div>

      {/* Section Tabs */}
      <Tabs value={activeSection} onValueChange={setActiveSection} className="w-full">
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="flock" className="flex items-center gap-1.5" data-testid="poultry-flock-tab">
            <Bird className="w-4 h-4" />
            <span className="hidden sm:inline">Flock</span>
          </TabsTrigger>
          <TabsTrigger value="eggs" className="flex items-center gap-1.5" data-testid="poultry-eggs-tab">
            <Egg className="w-4 h-4" />
            <span className="hidden sm:inline">Eggs</span>
          </TabsTrigger>
          <TabsTrigger value="incubation" className="flex items-center gap-1.5" data-testid="poultry-incubation-tab">
            <Baby className="w-4 h-4" />
            <span className="hidden sm:inline">Hatch</span>
          </TabsTrigger>
          <TabsTrigger value="costs" className="flex items-center gap-1.5" data-testid="poultry-costs-tab">
            <DollarSign className="w-4 h-4" />
            <span className="hidden sm:inline">Costs</span>
          </TabsTrigger>
        </TabsList>

        {/* FLOCK TAB */}
        <TabsContent value="flock" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-heading font-semibold">Your Flocks</h2>
            <Button 
              onClick={() => { resetFlockForm(); setEditingFlock(null); setShowFlockDialog(true); }}
              data-testid="add-flock-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Flock
            </Button>
          </div>

          {flocks.length === 0 ? (
            <Card className="card-farm">
              <CardContent className="py-12 text-center">
                <Bird className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No flocks yet</h3>
                <p className="text-muted-foreground mb-4">
                  Start by adding your first flock to track birds and production.
                </p>
                <Button onClick={() => setShowFlockDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Flock
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {flocks.map((flock) => {
                const trackedCount = allIndividualBirds.filter(
                  (b) => b.group_id === flock.id
                ).length;
                return (
                <Card 
                  key={flock.id} 
                  className={`card-farm cursor-pointer transition-all ${selectedFlock?.id === flock.id ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => { setSelectedFlock(flock); fetchEggRecords(flock.id); }}
                  data-testid={`flock-card-${flock.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-heading font-semibold text-lg">{flock.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs text-white ${
                            STATUS_OPTIONS.find(s => s.value === flock.status)?.color || 'bg-gray-500'
                          }`}>
                            {flock.status}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {flock.breed} • {flock.purpose}
                        </p>
                        <div className="flex items-center gap-4 mt-3 text-sm flex-wrap">
                          <span className="flex items-center gap-1">
                            <Bird className="w-4 h-4" />
                            {flock.bird_count} {flock.tracking_type === 'individual' ? 'bird' : 'birds'}
                          </span>
                          {(flock.female_count > 0 || flock.male_count > 0) && (
                            <span className="text-muted-foreground">
                              (<span className="text-pink-500">♀{flock.female_count || 0}</span> / <span className="text-blue-500">♂{flock.male_count || 0}</span>)
                            </span>
                          )}
                          {flock.tracking_type === 'cohort' && !flock.female_count && !flock.male_count && (
                            <span className="text-muted-foreground">Cohort tracking</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1" data-testid={`flock-count-detail-${flock.id}`}>
                          {flock.bird_count} in flock · {trackedCount} tracked individually
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingFlock(flock);
                            setFlockForm({
                              name: flock.name,
                              tracking_type: flock.tracking_type,
                              breed: flock.breed,
                              purpose: flock.purpose,
                              hatch_date: flock.hatch_date || '',
                              source: flock.source || '',
                              bird_count: flock.bird_count,
                              male_count: flock.male_count || 0,
                              female_count: flock.female_count || 0,
                              notes: flock.notes || ''
                            });
                            setShowFlockDialog(true);
                          }}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDeleteFlock(flock.id); }}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}

          {/* Selected Flock Details */}
          {selectedFlock && (
            <>
              <Card className="card-farm mt-6">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Bird className="w-5 h-5 text-primary" />
                    {selectedFlock.name} Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Breed</p>
                      <p className="font-medium">{selectedFlock.breed}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Purpose</p>
                      <p className="font-medium capitalize">{selectedFlock.purpose}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Total Birds</p>
                      <p className="font-medium">{selectedFlock.bird_count}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Gender Breakdown</p>
                      <p className="font-medium">
                        {(selectedFlock.female_count > 0 || selectedFlock.male_count > 0) ? (
                          <>
                            <span className="text-pink-500">♀{selectedFlock.female_count || 0}</span>
                            {' / '}
                            <span className="text-blue-500">♂{selectedFlock.male_count || 0}</span>
                          </>
                        ) : 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Source</p>
                      <p className="font-medium">{selectedFlock.source || 'Not specified'}</p>
                    </div>
                  </div>
                  {selectedFlock.notes && (
                    <div className="pt-2 border-t">
                      <p className="text-sm text-muted-foreground">Notes</p>
                      <p className="text-sm mt-1">{selectedFlock.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Individual Animal Tracking for Selected Flock */}
              <Card className="card-farm mt-4">
                <CardContent className="pt-6">
                  <IndividualAnimalTracker
                    animalType="poultry"
                    groupId={selectedFlock.id}
                    groupName={selectedFlock.name}
                    getAuthHeader={getAuthHeader}
                    breeds={CHICKEN_BREEDS}
                  />
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* EGGS TAB */}
        <TabsContent value="eggs" className="mt-6 space-y-4">
          {/* Quick Entry Section */}
          <Card className="card-farm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Egg className="w-5 h-5 text-amber-500" />
                Quick Egg Entry
              </CardTitle>
            </CardHeader>
            <CardContent>
              {flocks.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  Add a flock first to start tracking eggs.
                </p>
              ) : (
                <div className="space-y-3">
                  {flocks.filter(f => f.status === 'active' && (f.purpose === 'layers' || f.purpose === 'dual' || f.purpose === 'breeders')).map((flock) => (
                    <div key={flock.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div>
                        <p className="font-medium">{flock.name}</p>
                        <p className="text-sm text-muted-foreground">{flock.bird_count} birds</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          placeholder="0"
                          className="w-20 text-center"
                          id={`quick-egg-${flock.id}`}
                          data-testid={`quick-egg-input-${flock.id}`}
                        />
                        <Button
                          size="sm"
                          onClick={() => {
                            const input = document.getElementById(`quick-egg-${flock.id}`);
                            const count = parseInt(input.value) || 0;
                            if (count > 0) {
                              handleQuickEggEntry(flock.id, count);
                              input.value = '';
                            }
                          }}
                          data-testid={`quick-egg-submit-${flock.id}`}
                        >
                          Log
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="pt-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setShowEggDialog(true)}
                      data-testid="detailed-egg-entry-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Detailed Entry (with breed breakdown)
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Egg Stats */}
          {eggStats && (
            <div className="grid gap-4 md:grid-cols-3">
              <Card className="card-farm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">7-Day Average</p>
                      <p className="text-2xl font-heading font-bold">{eggStats.avg_7_day?.toFixed(1) || '0'}</p>
                      <p className="text-xs text-muted-foreground">eggs/day</p>
                    </div>
                    <Egg className="w-10 h-10 text-amber-500/30" />
                  </div>
                </CardContent>
              </Card>
              <Card className="card-farm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">30-Day Average</p>
                      <p className="text-2xl font-heading font-bold">{eggStats.avg_30_day?.toFixed(1) || '0'}</p>
                      <p className="text-xs text-muted-foreground">eggs/day</p>
                    </div>
                    <Calendar className="w-10 h-10 text-primary/30" />
                  </div>
                </CardContent>
              </Card>
              <Card className="card-farm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Lay Rate</p>
                      <p className="text-2xl font-heading font-bold">{eggStats.lay_rate?.toFixed(0) || '0'}%</p>
                      <p className="text-xs text-muted-foreground">
                        {eggStats.trend === 'up' && <span className="text-green-500 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Rising</span>}
                        {eggStats.trend === 'down' && <span className="text-red-500 flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Declining</span>}
                        {eggStats.trend === 'stable' && <span className="text-muted-foreground flex items-center gap-1"><Minus className="w-3 h-3" /> Stable</span>}
                      </p>
                    </div>
                    {eggStats.trend === 'up' ? (
                      <TrendingUp className="w-10 h-10 text-green-500/30" />
                    ) : eggStats.trend === 'down' ? (
                      <TrendingDown className="w-10 h-10 text-red-500/30" />
                    ) : (
                      <Minus className="w-10 h-10 text-muted-foreground/30" />
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Egg Production Calendar */}
          <ProductionCalendar
            data={eggCalendarData}
            title="Egg Production Calendar"
            unit="eggs"
            icon={Egg}
            colorTheme="amber"
            onDateClick={(date, total) => {
              toast.info(`${date}: ${total} eggs collected`);
            }}
          />

          {/* Recent Records */}
          {eggRecords.length > 0 && (
            <Card className="card-farm">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base">Recent Egg Records</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {eggRecords.slice(0, 7).map((record) => (
                    <div key={record.id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <p className="font-medium">{new Date(record.date).toLocaleDateString()}</p>
                        {record.notes && <p className="text-sm text-muted-foreground">{record.notes}</p>}
                      </div>
                      <div className="text-right">
                        <p className="font-heading font-bold text-lg">{record.count}</p>
                        <p className="text-xs text-muted-foreground">eggs</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* INCUBATION TAB */}
        <TabsContent value="incubation" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-heading font-semibold">Incubation Batches</h2>
            <Button 
              onClick={() => setShowIncubationDialog(true)}
              data-testid="add-incubation-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Start Batch
            </Button>
          </div>

          {incubationBatches.length === 0 ? (
            <Card className="card-farm">
              <CardContent className="py-12 text-center">
                <Baby className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No incubation batches</h3>
                <p className="text-muted-foreground mb-4">
                  Start tracking your hatching eggs.
                </p>
                <Button onClick={() => setShowIncubationDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Start First Batch
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {incubationBatches.map((batch) => {
                // Support both old API (set_date) and new API (start_date)
                const setDate = batch.set_date || batch.start_date;
                const dates = getIncubationDates(setDate);
                const daysToLockdown = getDaysUntil(dates.lockdown);
                const daysToHatch = getDaysUntil(dates.hatch);
                
                return (
                  <Card key={batch.id} className="card-farm" data-testid={`incubation-batch-${batch.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-heading font-semibold">{batch.name}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              batch.status === 'incubating' ? 'bg-yellow-100 text-yellow-800' :
                              batch.status === 'lockdown' ? 'bg-orange-100 text-orange-800' :
                              batch.status === 'hatched' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {batch.status}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {batch.eggs_set || batch.egg_count} eggs set • {batch.incubator_name || batch.hen_name || (batch.source === 'brooding_hen' ? 'Brooding Hen' : 'Incubator')}
                          </p>
                          
                          {/* Timeline */}
                          <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
                            <div className="p-2 rounded bg-muted/30">
                              <p className="text-xs text-muted-foreground">Set Date</p>
                              <p className="font-medium">{new Date(setDate).toLocaleDateString()}</p>
                            </div>
                            <div className={`p-2 rounded ${daysToLockdown <= 2 && daysToLockdown >= 0 ? 'bg-orange-100' : 'bg-muted/30'}`}>
                              <p className="text-xs text-muted-foreground">Lockdown</p>
                              <p className="font-medium">{new Date(dates.lockdown).toLocaleDateString()}</p>
                              {daysToLockdown > 0 && <p className="text-xs">{daysToLockdown} days</p>}
                              {daysToLockdown === 0 && <p className="text-xs text-orange-600 font-medium">Today!</p>}
                            </div>
                            <div className={`p-2 rounded ${daysToHatch <= 2 && daysToHatch >= 0 ? 'bg-green-100' : 'bg-muted/30'}`}>
                              <p className="text-xs text-muted-foreground">Hatch</p>
                              <p className="font-medium">{new Date(dates.hatch).toLocaleDateString()}</p>
                              {daysToHatch > 0 && <p className="text-xs">{daysToHatch} days</p>}
                              {daysToHatch === 0 && <p className="text-xs text-green-600 font-medium">Today!</p>}
                            </div>
                          </div>

                          {batch.notes && (
                            <p className="text-sm text-muted-foreground mt-2">{batch.notes}</p>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-col gap-1 ml-4">
                          {batch.status === 'incubating' && daysToLockdown <= 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateIncubationStatus(batch.id, 'lockdown')}
                            >
                              Enter Lockdown
                            </Button>
                          )}
                          {batch.status === 'lockdown' && daysToHatch <= 0 && (
                            <Button
                              size="sm"
                              onClick={() => {
                                const eggCount = batch.eggs_set || batch.egg_count;
                                const hatched = prompt('How many chicks hatched?', eggCount);
                                if (hatched !== null) {
                                  handleUpdateIncubationStatus(batch.id, 'hatched', { chicks_hatched: parseInt(hatched) });
                                }
                              }}
                            >
                              Record Hatch
                            </Button>
                          )}
                          {batch.status === 'hatched' && (
                            <div className="text-right">
                              <p className="text-sm font-medium text-green-600">
                                <CheckCircle2 className="w-4 h-4 inline mr-1" />
                                {batch.chicks_hatched || batch.hatch_count} hatched
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {(((batch.chicks_hatched || batch.hatch_count) / (batch.eggs_set || batch.egg_count)) * 100).toFixed(0)}% rate
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* COSTS TAB */}
        <TabsContent value="costs" className="mt-6 space-y-4">
          <Card className="card-farm">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                Cost Awareness
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-6">
                Track your inputs to understand production costs. All fields are optional.
              </p>
              
              <div className="space-y-6">
                {/* Cost per Dozen Calculator */}
                <div className="p-4 rounded-lg bg-muted/30">
                  <h3 className="font-medium mb-3">Quick Cost Estimate</h3>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <Label htmlFor="feed-cost">Monthly Feed Cost ($)</Label>
                      <Input id="feed-cost" type="number" placeholder="0.00" data-testid="feed-cost-input" />
                    </div>
                    <div>
                      <Label htmlFor="other-cost">Other Monthly Costs ($)</Label>
                      <Input id="other-cost" type="number" placeholder="0.00" data-testid="other-cost-input" />
                    </div>
                    <div>
                      <Label htmlFor="eggs-month">Eggs This Month</Label>
                      <Input 
                        id="eggs-month" 
                        type="number" 
                        placeholder={eggStats?.total_30_day || 0}
                        defaultValue={eggStats?.total_30_day || ''}
                        data-testid="eggs-month-input"
                      />
                    </div>
                  </div>
                  <Button
                    className="mt-4"
                    variant="outline"
                    onClick={() => {
                      const feed = parseFloat(document.getElementById('feed-cost').value) || 0;
                      const other = parseFloat(document.getElementById('other-cost').value) || 0;
                      const eggs = parseInt(document.getElementById('eggs-month').value) || 0;
                      if (eggs > 0) {
                        const costPerDozen = ((feed + other) / eggs) * 12;
                        toast.success(`Estimated cost: $${costPerDozen.toFixed(2)} per dozen`);
                      } else {
                        toast.error('Enter egg count to calculate');
                      }
                    }}
                    data-testid="calculate-cost-btn"
                  >
                    Calculate Cost per Dozen
                  </Button>
                </div>

                {/* Tips */}
                <div className="p-4 rounded-lg border border-amber-200 bg-amber-50/50">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">Cost Awareness Tips</h4>
                      <ul className="text-sm text-amber-700 mt-2 space-y-1">
                        <li>• Track feed purchases to identify consumption patterns</li>
                        <li>• Compare costs seasonally - feed needs change with weather</li>
                        <li>• Factor in bedding, supplements, and health products</li>
                        <li>• Cost per dozen helps set fair pricing for sales</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Market Planning - Merged with Market Insights */}
          <Card className="card-farm">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Market Planning
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Track your surplus and plan sales outlets.
              </p>
              
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="p-4 rounded-lg bg-muted/30">
                    <h4 className="font-medium mb-2">Surplus Tracking</h4>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="surplus-eggs">Weekly Surplus Eggs (dozens)</Label>
                        <Input id="surplus-eggs" type="number" placeholder="0" data-testid="surplus-eggs-input" />
                      </div>
                      <div>
                        <Label htmlFor="egg-price">Your Price per Dozen ($)</Label>
                        <Input id="egg-price" type="number" step="0.50" placeholder="5.00" data-testid="egg-price-input" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/30">
                    <h4 className="font-medium mb-2">Sales Outlets</h4>
                    <div className="space-y-2 text-sm">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" className="rounded" /> Farmers Market
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" className="rounded" /> CSA Share
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" className="rounded" /> Neighbors/Friends
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" className="rounded" /> On-Farm Sales
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" className="rounded" /> Restaurant/Wholesale
                      </label>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                  <h4 className="font-medium text-green-800 mb-2">Revenue Estimate</h4>
                  <p className="text-sm text-green-700">
                    Based on your inputs and current egg production, you could estimate potential weekly revenue.
                    This helps with planning but actual results will vary with demand and pricing.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      const surplus = parseFloat(document.getElementById('surplus-eggs').value) || 0;
                      const price = parseFloat(document.getElementById('egg-price').value) || 5;
                      const weekly = surplus * price;
                      const monthly = weekly * 4;
                      toast.success(`Potential: $${weekly.toFixed(2)}/week, $${monthly.toFixed(2)}/month`);
                    }}
                    data-testid="estimate-revenue-btn"
                  >
                    Estimate Revenue
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ADD/EDIT FLOCK DIALOG */}
      <Dialog open={showFlockDialog} onOpenChange={setShowFlockDialog}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader className="pb-2">
            <DialogTitle className="font-heading text-lg">
              {editingFlock ? 'Edit Flock' : 'Add New Flock'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label htmlFor="flock-name" className="text-sm">Flock Name *</Label>
              <Input
                id="flock-name"
                value={flockForm.name}
                onChange={(e) => setFlockForm({ ...flockForm, name: e.target.value })}
                placeholder="e.g., Main Layers"
                className="h-9"
                data-testid="flock-name-input"
              />
            </div>

            <div>
              <Label className="text-sm">Tracking Type</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  className={`p-2 rounded-lg border text-sm text-left transition-colors ${
                    flockForm.tracking_type === 'cohort' ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                  onClick={() => setFlockForm({ ...flockForm, tracking_type: 'cohort' })}
                >
                  <p className="font-medium text-sm">Cohort</p>
                  <p className="text-[10px] text-muted-foreground">Track as group</p>
                </button>
                <button
                  type="button"
                  className={`p-2 rounded-lg border text-sm text-left transition-colors ${
                    flockForm.tracking_type === 'individual' ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                  onClick={() => setFlockForm({ ...flockForm, tracking_type: 'individual' })}
                >
                  <p className="font-medium text-sm">Individual</p>
                  <p className="text-[10px] text-muted-foreground">Track each bird</p>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="breed" className="text-sm">Breed</Label>
                <select
                  id="breed"
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  value={flockForm.breed === '__custom__' ? '__custom__' : flockForm.breed}
                  onChange={(e) => {
                    if (e.target.value === '__custom__') {
                      setFlockForm({ ...flockForm, breed: '__custom__', customBreed: '' });
                    } else {
                      setFlockForm({ ...flockForm, breed: e.target.value, customBreed: '' });
                    }
                  }}
                  data-testid="flock-breed-select"
                >
                  <option value="">Select breed...</option>
                  {CHICKEN_BREEDS.map(breed => (
                    <option key={breed} value={breed}>{breed}</option>
                  ))}
                  <option value="__custom__">✨ Custom / Hybrid</option>
                </select>
                {flockForm.breed === '__custom__' && (
                  <Input
                    className="mt-1 h-8"
                    placeholder="Custom breed name"
                    value={flockForm.customBreed || ''}
                    onChange={(e) => setFlockForm({ ...flockForm, customBreed: e.target.value })}
                    data-testid="flock-custom-breed-input"
                  />
                )}
              </div>
              <div>
                <Label htmlFor="purpose" className="text-sm">Purpose</Label>
                <select
                  id="purpose"
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  value={flockForm.purpose}
                  onChange={(e) => setFlockForm({ ...flockForm, purpose: e.target.value })}
                  data-testid="flock-purpose-select"
                >
                  {PURPOSE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label htmlFor="bird-count" className="text-sm">Total</Label>
                <Input
                  id="bird-count"
                  type="number"
                  min="1"
                  className="h-9"
                  value={flockForm.bird_count}
                  onChange={(e) => setFlockForm({ ...flockForm, bird_count: parseInt(e.target.value) || 1 })}
                  data-testid="flock-count-input"
                />
              </div>
              <div>
                <Label htmlFor="female-count" className="text-sm flex items-center gap-1">
                  <span className="text-pink-500">♀</span> Hens
                </Label>
                <Input
                  id="female-count"
                  type="number"
                  min="0"
                  className="h-9"
                  value={flockForm.female_count}
                  onChange={(e) => setFlockForm({ ...flockForm, female_count: parseInt(e.target.value) || 0 })}
                  data-testid="flock-female-count-input"
                />
              </div>
              <div>
                <Label htmlFor="male-count" className="text-sm flex items-center gap-1">
                  <span className="text-blue-500">♂</span> Roosters
                </Label>
                <Input
                  id="male-count"
                  type="number"
                  min="0"
                  className="h-9"
                  value={flockForm.male_count}
                  onChange={(e) => setFlockForm({ ...flockForm, male_count: parseInt(e.target.value) || 0 })}
                  data-testid="flock-male-count-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="hatch-date" className="text-sm">Hatch Date</Label>
                <Input
                  id="hatch-date"
                  type="date"
                  className="h-9"
                  value={flockForm.hatch_date}
                  onChange={(e) => setFlockForm({ ...flockForm, hatch_date: e.target.value })}
                  data-testid="flock-hatch-date-input"
                />
              </div>
              <div>
                <Label htmlFor="source" className="text-sm">Source</Label>
                <Input
                  id="source"
                  className="h-9"
                  value={flockForm.source}
                  onChange={(e) => setFlockForm({ ...flockForm, source: e.target.value })}
                  placeholder="e.g., Local hatchery"
                  data-testid="flock-source-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes" className="text-sm">Notes</Label>
              <textarea
                id="notes"
                className="w-full min-h-[60px] px-3 py-2 rounded-md border border-input bg-background resize-none text-sm"
                value={flockForm.notes}
                onChange={(e) => setFlockForm({ ...flockForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                data-testid="flock-notes-input"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowFlockDialog(false); setEditingFlock(null); resetFlockForm(); }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={editingFlock ? handleUpdateFlock : handleCreateFlock}
                disabled={!flockForm.name || !flockForm.breed}
                data-testid="save-flock-btn"
              >
                {editingFlock ? 'Update' : 'Create'} Flock
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* DETAILED EGG ENTRY DIALOG */}
      <Dialog open={showEggDialog} onOpenChange={(open) => {
        setShowEggDialog(open);
        if (!open) {
          setFemaleHens([]);
          setEggForm(prev => ({ ...prev, individual_animal_id: '' }));
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Detailed Egg Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="egg-flock">Flock</Label>
              <select
                id="egg-flock"
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
                value={eggForm.flock_id}
                onChange={(e) => {
                  const flockId = e.target.value;
                  setEggForm({ ...eggForm, flock_id: flockId, individual_animal_id: '' });
                  if (flockId) {
                    fetchFemaleHensForFlock(flockId);
                  } else {
                    setFemaleHens([]);
                  }
                }}
                data-testid="egg-flock-select"
              >
                <option value="">Select flock...</option>
                {flocks.map(flock => (
                  <option key={flock.id} value={flock.id}>{flock.name}</option>
                ))}
              </select>
            </div>

            {/* Individual Hen Assignment (Optional) - Only shows if female hens exist */}
            {femaleHens.length > 0 && (
              <div>
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Assign to hen (optional)
                </Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background mt-1"
                  value={eggForm.individual_animal_id}
                  onChange={(e) => setEggForm({ ...eggForm, individual_animal_id: e.target.value })}
                  data-testid="egg-hen-select"
                >
                  <option value="">Flock total only</option>
                  {femaleHens.map(hen => (
                    <option key={hen.id} value={hen.id}>
                      {hen.name} {hen.tag_number ? `(#${hen.tag_number})` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  If assigned, eggs will also count toward flock total
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="egg-date">Date</Label>
                <Input
                  id="egg-date"
                  type="date"
                  value={eggForm.date}
                  onChange={(e) => setEggForm({ ...eggForm, date: e.target.value })}
                  data-testid="egg-date-input"
                />
              </div>
              <div>
                <Label htmlFor="egg-count">Total Eggs</Label>
                <Input
                  id="egg-count"
                  type="number"
                  min="0"
                  value={eggForm.count}
                  onChange={(e) => setEggForm({ ...eggForm, count: parseInt(e.target.value) || 0 })}
                  data-testid="egg-count-input"
                />
              </div>
            </div>

            <div>
              <Label>Entry Type</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  className={`p-2 rounded-lg border text-sm transition-colors ${
                    eggForm.entry_type === 'total' ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                  onClick={() => setEggForm({ ...eggForm, entry_type: 'total' })}
                >
                  Total Count Only
                </button>
                <button
                  type="button"
                  className={`p-2 rounded-lg border text-sm transition-colors ${
                    eggForm.entry_type === 'by_breed' ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                  onClick={() => setEggForm({ ...eggForm, entry_type: 'by_breed' })}
                >
                  By Breed
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="egg-notes">Notes (weather, events, etc.)</Label>
              <textarea
                id="egg-notes"
                className="w-full min-h-[60px] px-3 py-2 rounded-md border border-input bg-background resize-none"
                value={eggForm.notes}
                onChange={(e) => setEggForm({ ...eggForm, notes: e.target.value })}
                placeholder="Hot day, molting started, new feed..."
                data-testid="egg-notes-input"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowEggDialog(false)}>
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleDetailedEggEntry}
                disabled={!eggForm.flock_id || eggForm.count < 0}
                data-testid="save-egg-record-btn"
              >
                Save Record
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* INCUBATION DIALOG */}
      <Dialog open={showIncubationDialog} onOpenChange={setShowIncubationDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Egg className="w-5 h-5 text-amber-500" />
              Start Incubation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Source Type Toggle */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={incubationForm.source === 'incubator' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setIncubationForm({ ...incubationForm, source: 'incubator', hen_name: '' })}
              >
                🥚 Incubator
              </Button>
              <Button
                type="button"
                variant={incubationForm.source === 'brooding_hen' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setIncubationForm({ ...incubationForm, source: 'brooding_hen' })}
              >
                🐔 Brooding Hen
              </Button>
            </div>

            <div>
              <Label htmlFor="batch-name">Batch Name *</Label>
              <Input
                id="batch-name"
                value={incubationForm.name}
                onChange={(e) => setIncubationForm({ ...incubationForm, name: e.target.value })}
                placeholder={incubationForm.source === 'brooding_hen' ? "e.g., Henrietta's Spring Clutch" : "e.g., Spring Hatch 2026"}
                data-testid="batch-name-input"
              />
            </div>

            {/* Hen name for brooding */}
            {incubationForm.source === 'brooding_hen' && (
              <div>
                <Label htmlFor="hen-name">Hen's Name</Label>
                <Input
                  id="hen-name"
                  value={incubationForm.hen_name}
                  onChange={(e) => setIncubationForm({ ...incubationForm, hen_name: e.target.value })}
                  placeholder="Which hen is sitting?"
                  data-testid="hen-name-input"
                />
              </div>
            )}

            {/* Species selection */}
            <div>
              <Label htmlFor="species">Species</Label>
              <select
                id="species"
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
                value={incubationForm.species}
                onChange={(e) => setIncubationForm({ ...incubationForm, species: e.target.value })}
              >
                <option value="chicken">Chicken (21 days)</option>
                <option value="duck">Duck (28 days)</option>
                <option value="turkey">Turkey (28 days)</option>
                <option value="goose">Goose (30 days)</option>
                <option value="quail">Quail (17 days)</option>
                <option value="guinea">Guinea Fowl (28 days)</option>
              </select>
            </div>

            <div>
              <Label htmlFor="batch-flock">Source Flock</Label>
              <select
                id="batch-flock"
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
                value={incubationForm.flock_id}
                onChange={(e) => setIncubationForm({ ...incubationForm, flock_id: e.target.value })}
                data-testid="batch-flock-select"
              >
                <option value="">Select flock (optional)...</option>
                {flocks.map(flock => (
                  <option key={flock.id} value={flock.id}>{flock.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start-date">
                  {incubationForm.source === 'brooding_hen' ? 'Started Sitting *' : 'Set Date *'}
                </Label>
                <Input
                  id="start-date"
                  type="date"
                  value={incubationForm.start_date}
                  onChange={(e) => setIncubationForm({ ...incubationForm, start_date: e.target.value })}
                  data-testid="batch-start-date-input"
                />
              </div>
              <div>
                <Label htmlFor="egg-count">Eggs *</Label>
                <Input
                  id="egg-count"
                  type="number"
                  min="1"
                  value={incubationForm.egg_count}
                  onChange={(e) => setIncubationForm({ ...incubationForm, egg_count: parseInt(e.target.value) || 0 })}
                  data-testid="batch-egg-count-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="batch-notes">Notes</Label>
              <textarea
                id="batch-notes"
                className="w-full min-h-[60px] px-3 py-2 rounded-md border border-input bg-background resize-none"
                value={incubationForm.notes}
                onChange={(e) => setIncubationForm({ ...incubationForm, notes: e.target.value })}
                placeholder={incubationForm.source === 'brooding_hen' 
                  ? "Where is she nesting? Any special notes..."
                  : "Temperature, humidity settings, egg source..."
                }
                data-testid="batch-notes-input"
              />
            </div>

            {/* Preview dates */}
            {incubationForm.start_date && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm">
                <p className="font-medium mb-2 text-amber-800">
                  {incubationForm.source === 'brooding_hen' ? '🐔' : '🥚'} Projected Timeline
                </p>
                <div className="grid grid-cols-3 gap-2 text-amber-700">
                  <div>
                    <p className="text-amber-600 text-xs">Start</p>
                    <p>{new Date(incubationForm.start_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-amber-600 text-xs">Lockdown</p>
                    <p>{new Date(new Date(incubationForm.start_date).getTime() + 18 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-amber-600 text-xs">Expected Hatch</p>
                    <p className="font-medium">{new Date(new Date(incubationForm.start_date).getTime() + 
                      ({chicken: 21, duck: 28, turkey: 28, goose: 30, quail: 17, guinea: 28}[incubationForm.species] || 21) * 24 * 60 * 60 * 1000
                    ).toLocaleDateString()}</p>
                  </div>
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  ✓ Milestone reminders will be created automatically
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowIncubationDialog(false)}>
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleCreateIncubation}
                disabled={!incubationForm.name || !incubationForm.start_date || incubationForm.egg_count < 1}
                data-testid="start-incubation-btn"
              >
                {incubationForm.source === 'brooding_hen' ? '🐔 Start Tracking' : '🥚 Start Batch'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deep Link Animal Profile Dialog */}
      <Dialog open={showDeepLinkDialog} onOpenChange={setShowDeepLinkDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {deepLinkAnimal && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  {deepLinkAnimal.photo_url ? (
                    <img 
                      src={deepLinkAnimal.photo_url} 
                      alt={deepLinkAnimal.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <Star className="w-6 h-6 text-amber-500" />
                  )}
                  {deepLinkAnimal.name}
                  <span className={`text-sm ${deepLinkAnimal.gender === 'female' || deepLinkAnimal.sex === 'female' ? 'text-pink-500' : 'text-blue-500'}`}>
                    {(deepLinkAnimal.gender === 'female' || deepLinkAnimal.sex === 'female') ? '♀' : 
                     (deepLinkAnimal.gender === 'male' || deepLinkAnimal.sex === 'male') ? '♂' : '?'}
                  </span>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-2">
                {/* Animal Info */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Breed</p>
                    <p className="font-medium">{deepLinkAnimal.breed || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tag #</p>
                    <p className="font-medium">{deepLinkAnimal.tag_number || '-'}</p>
                  </div>
                  {deepLinkAnimal.birth_date && (
                    <div>
                      <p className="text-muted-foreground">Birth Date</p>
                      <p className="font-medium">{new Date(deepLinkAnimal.birth_date).toLocaleDateString()}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <p className="font-medium capitalize">{deepLinkAnimal.status || 'Active'}</p>
                  </div>
                </div>

                {deepLinkAnimal.notes && (
                  <div className="p-3 rounded-lg bg-muted/30 text-sm">
                    <p className="text-muted-foreground text-xs mb-1">Notes</p>
                    <p>{deepLinkAnimal.notes}</p>
                  </div>
                )}

                {/* Event History */}
                {deepLinkAnimal.events && deepLinkAnimal.events.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-2">Recent History</h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {deepLinkAnimal.events.slice(0, 5).map((event, idx) => (
                        <div key={idx} className="text-sm p-2 rounded bg-muted/20">
                          <div className="flex justify-between">
                            <span className="font-medium capitalize">{event.event_type}</span>
                            <span className="text-muted-foreground text-xs">
                              {event.date && new Date(event.date).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-muted-foreground">{event.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Close Button */}
                <div className="flex justify-end pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowDeepLinkDialog(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PoultryManager;
